import { useRef, useEffect, useState, startTransition } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import FilterModeSelector from '../FilterModeSelector/FilterModeSelector';
import FilterMessageLimits from '../FilterMessageLimits/FilterMessageLimits';
import FilterMessageDates from '../FilterMessageDates/FilterMessageDates';
import ActiveUser from '../ActiveUser/ActiveUser';

import * as Style from './style';
import {
  activeUserAtom,
  isAnonymousAtom,
  isMenuOpenAtom,
  messagesDateBoundsAtom,
  participantsAtom,
} from '../../constant/global';
import {
  datesAtom,
  globalFilterModeAtom,
  limitsAtom,
} from '../../constant/filters';

function Sidebar() {
  const [isMenuOpen, setIsMenuOpen] = useAtom(isMenuOpenAtom);
  const [isAnonymous, setIsAnonymous] = useAtom(isAnonymousAtom);
  const [filterMode, setFilterMode] = useState('index');
  const setGlobalFilterMode = useSetAtom(globalFilterModeAtom);
  const [limits, setLimits] = useAtom(limitsAtom);
  const setDates = useSetAtom(datesAtom);
  const messagesDateBounds = useAtomValue(messagesDateBoundsAtom);
  const participants = useAtomValue(participantsAtom);
  const [activeUser, setActiveUser] = useAtom(activeUserAtom);

  const closeButtonRef = useRef(null);
  const openButtonRef = useRef(null);

  const setMessageLimits = (e) => {
    const entries = Object.fromEntries(new FormData(e.currentTarget));

    e.preventDefault();
    setLimits({
      low: parseInt(entries.lowerLimit, 10),
      high: parseInt(entries.upperLimit, 10),
    });
    setGlobalFilterMode('index');
  };

  const setMessagesByDate = (e) => {
    e.preventDefault();
    setDates({
      start: e.currentTarget.startDate.valueAsDate,
      end: e.currentTarget.endDate.valueAsDate,
    });
    setGlobalFilterMode('date');
  };

  useEffect(() => {
    const keyDownHandler = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('keydown', keyDownHandler);
    return () => document.removeEventListener('keydown', keyDownHandler);
  }, [setIsMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) closeButtonRef.current?.focus();
    else openButtonRef.current?.focus();
  }, [isMenuOpen]);

  return (
    <>
      <Style.MenuOpenButton
        className="menu-open-button"
        type="button"
        onClick={() => setIsMenuOpen(true)}
        ref={openButtonRef}
      >
        Open menu
      </Style.MenuOpenButton>
      <Style.Overlay
        type="button"
        isActive={isMenuOpen}
        onClick={() => setIsMenuOpen(false)}
        tabIndex={-1}
      />
      <Style.Sidebar isOpen={isMenuOpen}>
        <Style.MenuCloseButton
          type="button"
          onClick={() => setIsMenuOpen(false)}
          ref={closeButtonRef}
        >
          Close menu
        </Style.MenuCloseButton>
        <Style.SidebarContainer>
          <Style.SidebarChildren>
            <FilterModeSelector
              filterMode={filterMode}
              setFilterMode={setFilterMode}
            />
            {filterMode === 'index' && (
              <FilterMessageLimits
                limits={limits}
                setMessageLimits={setMessageLimits}
              />
            )}
            {filterMode === 'date' && (
              <FilterMessageDates
                messagesDateBounds={messagesDateBounds}
                setMessagesByDate={setMessagesByDate}
              />
            )}
            <ActiveUser
              participants={participants}
              activeUser={activeUser}
              setActiveUser={setActiveUser}
            />

            <Style.Field>
              <Style.Label htmlFor="is-anonymous">Anonymize users</Style.Label>
              <Style.ToggleCheckbox
                id="is-anonymous"
                type="checkbox"
                checked={isAnonymous}
                onChange={() =>
                  startTransition(() => setIsAnonymous((bool) => !bool))
                }
              />
            </Style.Field>
          </Style.SidebarChildren>
         
        </Style.SidebarContainer>
      </Style.Sidebar>
    </>
  );
}

export default Sidebar;
